/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import JsonQuery from 'json-query';
import parse from 'parse-json';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import {
    getColorPalette,
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import ChartTableStyles, { chartThemeTableStyles } from './ChartTable.styles';
import { sampleData } from './sampleData';

interface TableColumn {
    label?: string;
    value?: string;
    isIncludeColor?: boolean;
    isOnlyText?: boolean;
}

interface TableProps {
    dataKey?: string;
    headerRow?: string;
    columns?: TableColumn[];
    colors?: string;
    typographyClass?: string;
    disableTabStaticData?: boolean;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, TableProps {}

interface Widgetprops extends commonTypes.WidgetInstanceProps, TableProps {}

const ChartTable = (props: Widgetprops) => {
    const { data } = props;
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const { disableTabStaticData: globalDisableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};
    const { styles, dataKey, headerRow, columns, colors, disableTabStaticData } =
        widgetProperties || props;

    const swatchColors = colors ? parse(colors) : getColorPalette();
    const chartData: any = dataKey ? JsonQuery(`${dataKey}`, { data }).value : sampleData;

    const sortedData = Array.isArray(chartData)
        ? parse(JSON.stringify(chartData)).sort((a: any, b: any) => a.sortOrder - b.sortOrder)
        : [];

    const i18n = commonHooks.useLocalization();

    const theme = data?.themeSelected || props?.theme;
    const tabIndex = globalDisableTabStaticData || disableTabStaticData ? -1 : 0;

    return (
        <div
            className={`table-wrapper ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            }`}
            css={css`
                ${!theme ? ChartTableStyles : chartThemeTableStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
        >
            {headerRow && (
                <div className="table-header">
                    {headerRow
                        .toString()
                        .split(',')
                        .map((h, id) => (
                            <span
                                tabIndex={tabIndex}
                                key={`header--${id}`}
                                className={`header--${id}`}
                                dangerouslySetInnerHTML={{ __html: h || '' }}
                            />
                        ))}
                </div>
            )}
            <table className="table--plain table--holdings">
                <thead>
                    <tr>
                        {columns?.map((c: TableColumn, i: number) => (
                            <th
                                tabIndex={tabIndex}
                                scope="col"
                                key={i}
                                dangerouslySetInnerHTML={{ __html: c.label || '' }}
                            />
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sortedData?.length
                        ? sortedData.map((r: any, i: number) => (
                              <tr key={i} className="allocation-row">
                                  {columns?.map((c: TableColumn, id: number) => {
                                      const boundHtml =
                                          (c.value && i18n.format(c.value, r)) ||
                                          i18n.t('', '_', 'dash');
                                      return (
                                          <td key={r + id} className={`col--${id}`}>
                                              {c.label === 'swatch' ? (
                                                  <>
                                                      <span
                                                          className="color-swatch"
                                                          style={{
                                                              backgroundColor:
                                                                  swatchColors?.[
                                                                      i % swatchColors?.length
                                                                  ],
                                                          }}
                                                      />
                                                      <span
                                                          className="sr-only"
                                                          dangerouslySetInnerHTML={{
                                                              __html: boundHtml,
                                                          }}
                                                      />
                                                  </>
                                              ) : (
                                                  <>
                                                      {c.isIncludeColor && (
                                                          // decorative image
                                                          <img
                                                              src={''}
                                                              alt={''}
                                                              className="color-swatch"
                                                              style={{
                                                                  backgroundColor:
                                                                      swatchColors?.[
                                                                          i % swatchColors?.length
                                                                      ],
                                                              }}
                                                          />
                                                      )}
                                                      <span
                                                          key={`${c.value}${id}`}
                                                          tabIndex={tabIndex}
                                                          dangerouslySetInnerHTML={{
                                                              __html: boundHtml,
                                                          }}
                                                      />
                                                  </>
                                              )}
                                          </td>
                                      );
                                  })}
                              </tr>
                          ))
                        : null}
                </tbody>
            </table>
        </div>
    );
};

export default ChartTable;
